import { Injectable, OnDestroy } from '@angular/core';
import { Coordinates } from '../../lead.model';
import {
  first,
  firstValueFrom,
  interval,
  Observable,
  ReplaySubject,
} from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class GeolocationService implements OnDestroy {
  private readonly currentPosition = new ReplaySubject<GeolocationPosition>(1);
  currentPosition$: Observable<GeolocationPosition> =
    this.currentPosition.asObservable();
  private handle: number | undefined = undefined;

  getPosition(): Promise<GeolocationPosition> {
    return firstValueFrom(this.currentPosition$.pipe(first()));
  }

  triggerWatch() {
    // Clear any existing geolocation watch to avoid conflicts or duplicate updates
    if (this.handle !== undefined) {
      navigator.geolocation.clearWatch(this.handle);
      this.handle = undefined;
    }

    this.setupPositionUpdates();
  }

  /**
   * formerly known as checkDistance
   * @param {Coordinates} coordinates
   */
  async currentDistanceFromPositionCloseEnough(
    coordinates: Coordinates,
  ): Promise<boolean> {
    const position = await this.getPosition();
    const distance = calculateDistanceInMeters(coordinates, position.coords);
    // updated to ensure the distance with the accuracy margin is within the allowed max distance
    return (
      distance - position.coords.accuracy <= environment.allowed_max_distance_m
    );
  }

  private setupPositionUpdates() {
    if (environment.mock_position) {
      const mock_position = environment.mock_position;
      interval(1000).subscribe(() => {
        this.currentPosition.next({
          coords: {
            latitude: mock_position.latitude,
            longitude: mock_position.longitude,
            accuracy: mock_position.accuracy,
            altitude: null,
            altitudeAccuracy: null,
            heading: null,
            speed: null,
            toJSON: () => '',
          },
          timestamp: Date.now(),
          toJSON: () => '',
        });
      });
    } else {
      this.handle = navigator.geolocation.watchPosition(
        (position) => {
          this.currentPosition.next(position);
          console.debug('Position updated: ', position);
        },
        (error) => {
          console.error(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          // increased to 10 seconds for less frequent updates (from 5 seconds)
          maximumAge: 10000,
        },
      );
    }
  }

  ngOnDestroy() {
    if (this.handle !== undefined) {
      navigator.geolocation.clearWatch(this.handle);
      this.handle = undefined;
    }
  }
}

export function calculateDistanceInMeters(
  position1: Coordinates,
  position2: Coordinates,
): number {
  const lat1Rad = (position1.latitude * Math.PI) / 180;
  const long1Rad = (position1.longitude * Math.PI) / 180;
  const lat2Rad = (position2.latitude * Math.PI) / 180;
  const long2Rad = (position2.longitude * Math.PI) / 180;
  const dLat = lat2Rad - lat1Rad;
  const dLong = long2Rad - long1Rad;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(dLong / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const R = 6371001; // Radius of Earth in meters
  return R * c;
}
